import {createRouter, createWebHistory} from 'vue-router'
import {useAuthDataStore} from "@/stores/auth-data";
import HomeView from '@/views/HomeView.vue';
import {useAppDataStore} from "@/stores/app-data";
import {useUxData} from "@/stores/ux-data";
import {useCharacterDataStore} from "@/stores/character-data";
import type {CharacterSheetInterface} from "@/types/characters.ts";
import type {WorldInterface} from "@/types/worlds.ts";
import {SAVED_ROUTE_KEY} from "@/appdata/globals";
import {adminRedirect} from "@/router/guards";

function clearCharacterData() {
  const store = useAppDataStore();
  if (store.character?.uuid) {
    store.character = null;
  }
}

async function loadSystemData(opts: any) {
  const store = useAppDataStore();
  await store.loadSystemContext(opts?.system ?? import.meta.env.VITE_SYSTEM_ALIAS);
}

const confirmUnsavedChanges = (): boolean => {
  const store = useAppDataStore();
  const ux = useUxData();

  if (!store.props.edit) return true;

  return confirm(ux.labels.messages.unsavedChanges);
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      meta: { public: true },
      component:  () => import('../views/Auth.vue')
    },
    {
      path: '/',
      name: 'home',
      component:  () => import('../views/HomeView.vue')
    },
    {
      path: '/:system/characters',
      name: 'characters',
      component: () => import('../views/CharacterCreatorView.vue'),
      props: true,
      beforeEnter: (to, from, next) => {
        clearCharacterData();
        next();
      }
    },
    {
      path: '/:system/data/:category?',
      name: 'data-manager',
      component: () => import('../views/DataManagement.vue'),
      beforeEnter: (to, from, next) => {
        // console.debug(to);
        loadSystemData(to.params);
        next();
      },
    },
    {
      path: '/characters/:character',
      name: 'character',
      component: () => import('../views/CharacterCreatorView.vue'),
      props: true,
    },
    {
      path: '/user/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue')
    },
    {
      path: '/notifications',
      name: 'inbox',
      component: () => import('../views/NotificationInbox.vue')
    },
    {
      path: '/admin/dashboard',
      name: 'admin-dashboard',
      meta: { admin: true },
      component: () => import('../views/AdminDashboardView.vue')
    },
    {
      path: '/invite/:uuid',
      name: 'invitation',
      component: () => import('../views/InvitationResponseView.vue'),
    },
  ]
})

router.beforeEach(async (to, from, next) => {
  // console.info("router.beforeEach | ", to, from)

  // Unsaved changes guard (character editor)
  if ((from.name === 'character') && (from.fullPath !== to.fullPath)) {
    const confirmLeave = confirmUnsavedChanges()
    if (confirmLeave) {
      useAppDataStore().props.edit = false;
    }
    else {
      next(false)
      return;
    }
  }

  // Auth guard: only check after Firebase has initialized
  const auth = useAuthDataStore();
  if (!to.meta?.public && auth.initialized && !auth.authenticated) {
    sessionStorage.setItem(SAVED_ROUTE_KEY, to.fullPath);
    next({ name: 'login' });
    return;
  }

  // Admin guard: runs after the auth check so an unauthenticated visitor still reaches
  // login with their destination saved, rather than being bounced to home.
  const adminTarget = adminRedirect(to, auth);
  if (adminTarget) {
    next(adminTarget);
    return;
  }

  next();
});

export default router
